import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import type { FunctionComponent, PropsWithChildren } from 'react';

import { Button } from '~/ui/core/button/Button';
import { theme } from '~/ui/theme/cobrainer-theme';

import errorIllustration2x from './error-illustration@2x.png';
import errorIllustration from './error-illustration.png';

type ErrorPageProps = PropsWithChildren<{
    readonly errorCode?: number;
    readonly message: string;
    readonly title: string;
}>;

const Container = styled.div(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.colors.white[200],
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: `${theme.spacing['10xl']} ${theme.spacing['13xl']}`,
}));

const ContentWrapper = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing['10xl'],
    maxWidth: '26rem',
}));

const LogoSection = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
}));

const Logo = styled.div({
    height: '1rem',
    width: '2.2rem',
});

// biome-ignore lint/correctness/noEmptyPattern: suppressed due to migration
const Content = styled.div(({}) => ({
    display: 'flex',
    flexDirection: 'column',
}));

// biome-ignore lint/correctness/noEmptyPattern: suppressed due to migration
const Headline = styled.div(({}) => ({
    display: 'flex',
    flexDirection: 'column',
}));

const Title = styled.h1(({ theme }) => ({
    ...theme.typography.headline.h4.regular,
    color: theme.colors.black[900],
    margin: 0,
}));

const Message = styled.p(({ theme }) => ({
    ...theme.typography.body.S.regular,
    color: theme.colors.gray[900],
    margin: 0,
}));

const ButtonsContainer = styled.div(({ theme }) => ({
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing.m,
}));

const IllustrationContainer = styled.div(({ theme }) => ({
    marginTop: theme.spacing['10xl'],
}));

const Illustration = styled.div(({ theme }) => ({
    '@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)': {
        backgroundImage: `url(${errorIllustration2x})`,
    },
    backgroundImage: `url(${errorIllustration})`,
    backgroundPosition: '10% 50%',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100%',
    borderRadius: theme.borderRadius.xxxl,
    boxShadow: theme.shadows.large,
    minHeight: '61.625rem',
    overflow: 'hidden',
    width: '26rem',
}));

const mailtoLink = 'mailto:feedback@cobrainer.com';
const subject = 'Error Cobrainer App';

const mailto = (): void => {
    window.open(`${mailtoLink}?subject=${subject}`);
};

export const ErrorPage: FunctionComponent<ErrorPageProps> = (props) => (
    <ThemeProvider theme={theme}>
        <Container>
            <ContentWrapper>
                <Content>
                    <LogoSection>
                        <Logo />
                    </LogoSection>
                    <Headline>
                        <Title>{props.title}</Title>
                        <Message>{props.message}</Message>
                    </Headline>
                </Content>
                <ButtonsContainer>
                    <Button size="sm" variant="secondary" onClick={mailto}>
                        Provide feedback
                    </Button>
                    <Button size="sm" variant="default" onClick={() => (window.location.href = '/')}>
                        Reload
                    </Button>
                </ButtonsContainer>
            </ContentWrapper>
            <IllustrationContainer>
                <Illustration />
            </IllustrationContainer>
        </Container>
    </ThemeProvider>
);
